import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Sparkles, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function Demo() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", course: "IELTS Coaching", preferred_time: "Evening (6-9 PM)", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/demo-booking", form);
      setSubmitted(true);
      toast.success("Demo booked! Our team will reach out within 24 hours.");
    } catch {
      toast.error("Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-28 pb-24 ali-hero-gradient min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="ali-chip mx-auto"><Sparkles className="w-3.5 h-3.5" /> 100% Free · No Card</div>
          <h1 className="mt-4 font-satoshi font-black text-4xl sm:text-5xl text-[#0A192F] tracking-tight">Book your free demo class.</h1>
          <p className="mt-3 text-slate-500 font-inter max-w-xl mx-auto">Experience a real live class. Meet your potential instructor. Decide if it's the right fit — zero pressure.</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mt-10 ali-card p-8" data-testid="demo-form-card">
          {submitted ? (
            <div className="text-center py-10">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h2 className="mt-4 font-satoshi font-bold text-2xl text-[#0A192F]">You're in!</h2>
              <p className="mt-2 text-slate-600 font-inter">We'll WhatsApp you a confirmation and your demo class link within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-poppins">Full Name</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6B35] font-inter" data-testid="demo-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-poppins">Email</label>
                <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6B35]" data-testid="demo-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-poppins">Phone</label>
                <input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6B35]" data-testid="demo-phone" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-poppins">Interested Course</label>
                <select value={form.course} onChange={e => setForm({ ...form, course: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6B35]" data-testid="demo-course">
                  {["IELTS Coaching","Spoken English","German Language","Spanish Language","French Language","Japanese Language","Arabic Language","Study Abroad","Personality Development","Interview Skills"].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-poppins">Preferred Time</label>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {["Morning (8-11 AM)","Afternoon (1-4 PM)","Evening (6-9 PM)"].map(t => (
                    <button type="button" key={t} onClick={() => setForm({ ...form, preferred_time: t })}
                      className={`px-3 py-2 rounded-xl text-sm font-poppins border ${form.preferred_time === t ? "bg-[#0A192F] text-white border-transparent" : "border-slate-200 text-slate-600"}`}
                      data-testid={`demo-time-${t.split(' ')[0]}`}
                    >{t}</button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-poppins">Message (optional)</label>
                <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-[#FF6B35]" data-testid="demo-message" />
              </div>
              <div className="md:col-span-2">
                <button disabled={loading} className="w-full ali-btn-primary flex items-center justify-center gap-2 disabled:opacity-60" data-testid="demo-submit">
                  <Calendar className="w-4 h-4" /> {loading ? "Booking…" : "Book My Free Demo"}
                </button>
                <p className="mt-3 text-center text-xs text-slate-400 font-inter">No spam. No pressure. Just an honest free demo.</p>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </main>
  );
}
